import bbox from '@turf/bbox';
import mapboxgl from "mapbox-gl";
/**
  Convert a list into a JS object
  @function listToMap
  @param {List} list List to convert into a map
  @param {String} propName Property to use as key
  @returns {Object}
*/
function listToMap(list, propName = 'id') {
	return list.reduce(
		(map, item) => ({
			...map,
			[item[propName].toLowerCase()]: item
		}),
		{}
	);
};


/**
  Populate a property by obtaining the value from a map
  @function populateFromMap
  @param {List} list List of objects to populate
  @param {String} propertyName Property to populate
  @param {Object} mapByList Map of objects to use to populate
  @param {String} idPropName Property name to match as the key
  @returns {List}
*/
function populateFromMap(
	list,
	propertyName,
	mapByList,
	idPropName = 'id'
) {
	return list.map(item => {
		let value = item[propertyName];
		let propValue =
			value && value[idPropName] && value[idPropName].toLowerCase();
		if (propValue && propValue in mapByList) {
			item[propertyName] = mapByList[propValue]
		}
		return item;
	});
};

function fitPolygon(map, polygon) {
	map.fitBounds(
		bbox({
			type: 'Feature',
			geometry: {
				type: 'Polygon',
				coordinates: [polygon],
			},
		}),
		{ linear: true, padding: { top: 25, bottom: 25, right: 25, left: 25 } }
	);
}

function pointsToString(points) {
	let val = '';
	if (points) {
		for (let index = 0; index < points.length; index++) {
			const point = points[index];
			val += `${point[0]},${point[1]}`;
			index !== points.length - 1 ? (val += ';') : null;
		}
	}
	return val;
}

function pointsInsidePolygon(points, polygon) {
	let inside = false;
	if (points && polygon) {
		for (let index = 0; index < points.length; index++) {
			inside = false;
			const point = points[index];
			let x = point[0], y = point[1];
			for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
				let xi = polygon[i][0], yi = polygon[i][1];
				let xj = polygon[j][0], yj = polygon[j][1];
				let intersect = ((yi > y) != (yj > y))
					&& (x < (xj - xi) * (y - yi) / (yj - yi) + xi);
				if (intersect) inside = !inside;
			}
			if (!inside) {
				return inside;
			}
		}
	}
	return inside;
}

function checkExistingPointInDraw(draw, data) {
	let result = draw.get(data.id);
	return !!result;
}

function addPolygonToMap(map, polygon, id, name) {
	const sourceId = `readonly-${id}`;
	map.addSource(sourceId, {
		'type': 'geojson',
		'data': {
			'type': 'Feature',
			'properties': {
				'description': name || ''
			},
			'geometry': {
				'type': 'Polygon',
				'coordinates': [polygon]
			}
		}
	});
	return sourceId;
}

function addPointToMap(map, point, id, name) {
	map.addSource(`readonly-${id}`, {
		'type': 'geojson',
		'data': {
			'type': 'Feature',
			'properties': {
				'description': name || '',
				'title': name || ''
			},
			'geometry': {
				'type': 'Point',
				'coordinates': [point[0], point[1]]
			}
		}
	});
}

function addPointTodraw(mapBoxDraw, id, name, point) {
	let feature = {
		id: id,
		type: 'Feature',
		properties: {
			name: name,
		},
		geometry: { type: 'Point', coordinates: point },
	};
	mapBoxDraw.add(feature);
}

function addLayerNameToMap(map, id) {
	map.addLayer({
		'id': `${id}-labels`,
		'type': 'symbol',
		'source': `readonly-${id}`,
		'layout': {
			'text-field': ['get', 'description'],
			'text-variable-anchor': ['center'],
			'text-allow-overlap': false,
			'text-radial-offset': 0.5,
			'text-justify': 'center'
		}
	});
}
function addLayerNameToMapPoint(map, id) {
	map.addLayer({
		'id': `${id}-labels`,
		'type': 'symbol',
		'source': `readonly-${id}`,
		'layout': {
			'text-field': ['get', 'description'],
			'text-variable-anchor': ['top', 'bottom', 'left', 'right'],
			'text-radial-offset': 0.5,
			'text-justify': 'auto'
		}
	});
}
function addFillLayerToMap(map, id, fillColor) {
	map.addLayer({
		'id': id,
		'type': 'fill',
		'source': `readonly-${id}`,
		'layout': {},
		'paint': {
			'fill-color': fillColor,
			'fill-opacity': 0.3
		}
	});
}
function addFillPolygonAndLineToMap(map, id, fillColor) {
	map.addLayer({
		id: `fill-${id}`,
		type: 'fill',
		source: id,
		paint: {
			'fill-color': fillColor,
			'fill-opacity': 0.1,
		},
	});

	map.addLayer({
		id: `line-${id}`,
		type: 'line',
		source: id,
		paint: {
			'line-width': 6,
			'line-color': fillColor,
		},
	});
}

function getMapStyles(id, context) {
	return [
		{
			id: `${id}-draw-polygon-fill`,
			type: 'fill',
			paint: {
				'fill-color': context.$vuetify.theme.themes.light.secondary,
				'fill-outline-color': context.$vuetify.theme.themes.light.secondary,
				'fill-opacity': 0.3,
			},
		},
		{
			id: `${id}-draw-polygon-line`,
			type: 'line',
			paint: {
				'line-width': 3,
				'line-color': context.$vuetify.theme.themes.light.primary,
			},
		},
		{
			id: `${id}-draw-polygon-circle`,
			type: 'circle',
			paint: {
				'circle-radius': 7,
				'circle-color': context.$vuetify.theme.themes.light.primary,
			},
		},
	]
}

function getMapInitOptions(container) {
	return {
		container: container,
		center: [-99.5, 41.8],
		zoom: 3,
		dragPan: true,
		style: 'mapbox://styles/mapbox/streets-v9',
		interactive: true,
	}
}

function drawAreaIndoorPlan(map, area) {
	const { nwCorner, seCorner } = area;
	const file = area.file[0].preview;
	const areaOverlayLayerId = 'indoor-area-overlay-airfinder';

	if (!map.getLayer(areaOverlayLayerId)) {
		map.addSource(areaOverlayLayerId, {
			type: 'image',
			url: file,
			coordinates: [
				[nwCorner.lng, nwCorner.lat],
				[seCorner.lng, nwCorner.lat],
				[seCorner.lng, seCorner.lat],
				[nwCorner.lng, seCorner.lat]
			]
		});

		map.addLayer(
			{
				id: areaOverlayLayerId,
				source: areaOverlayLayerId,
				type: 'raster'
			}
		);

		fitPolygon(map, [
			[nwCorner.lng, nwCorner.lat],
			[seCorner.lng, nwCorner.lat],
			[seCorner.lng, seCorner.lat],
			[nwCorner.lng, seCorner.lat]
		]);

		map.setMaxBounds([
			[nwCorner.lng - 0.3, seCorner.lat - 0.1],
			[seCorner.lng + 0.3, nwCorner.lat + 0.1],
		]);
	}
}

let onHistoryLayerClickHandler;
let onHistoryLayerMouseEnterHandler;
let onHistoryLayerMouseLeaveHandler;

function drawHistory(map, history, onHistoryClick) {
	const tagHistoryLayer = 'tag-history-airfinder';
	const previousHistoryLayer = map.getLayer(tagHistoryLayer);
	if (previousHistoryLayer) {
		map.removeLayer(previousHistoryLayer);
	}
	if (history.features.length) {
		let historySource = map.getSource(tagHistoryLayer);
		if (historySource) {
			historySource.setData(history);
		} else {
			map.addSource(tagHistoryLayer, {
				type: 'geojson',
				data: history,
			});
		}
		map.addLayer({
			id: tagHistoryLayer,
			source: tagHistoryLayer,
			type: 'symbol',
			layout: {
				'icon-image': [
					'match',
					['get', 'etype'],
					'wifi',
					'wifi',
					'gps',
					'gps',
					'cellid',
					'cell',
					'cellId',
					'cell',
					'gps',
				],
				'icon-allow-overlap': true,
			},
		});

		const coordinates = history.features.map((tag) => {
			return tag.geometry.coordinates;
		});

		if (onHistoryLayerClickHandler) {
			map.off('click', tagHistoryLayer, onHistoryLayerClickHandler);
			map.off('mouseenter', tagHistoryLayer, onHistoryLayerMouseEnterHandler);
			map.off('mouseleave', tagHistoryLayer, onHistoryLayerMouseLeaveHandler);
		}

		onHistoryLayerClickHandler = (e) => {
			const eventId = e.features[0].properties.id;
			onHistoryClick(eventId);
		}

		onHistoryLayerMouseEnterHandler = () => (map.getCanvas().style.cursor = 'pointer');
		onHistoryLayerMouseLeaveHandler = () => (map.getCanvas().style.cursor = '');

		map.on('click', tagHistoryLayer, onHistoryLayerClickHandler);
		map.on('mouseenter', tagHistoryLayer, onHistoryLayerMouseEnterHandler);
		map.on('mouseleave', tagHistoryLayer, onHistoryLayerMouseLeaveHandler);

		fitPolygon(map, coordinates);
	}
}

function removeMarker(map) {
	if (map.getLayer('selected-event')) {
		map.removeLayer('selected-event');
		map.removeSource('selected-event');
	}
}

function createMarker(coords, map, color, strokeColor, strokeOpacity, strokeWidth, translate) {
	removeMarker(map);

	let source = {
		type: 'geojson',
		data: {
			type: 'Feature',
			geometry: {
				type: 'Point',
				coordinates: coords,
			},
		},
	};

	map.addSource('selected-event', source);
	map.addLayer({
		id: 'selected-event',
		source: 'selected-event',
		type: 'circle',
		paint: {
			'circle-radius': {
				stops: [
					[1, 4],
					[8, 6],
					[10, 8],
				],
			},
			'circle-color': color || '#faff67',
			'circle-blur': 0,
			'circle-stroke-width': strokeWidth || 6,
			'circle-stroke-opacity': strokeOpacity || 0,
			'circle-stroke-color': strokeColor || '#faff67',
			'circle-translate': translate || [0, -7],
		},
	});
}

function getFirstLayer(map) {
	try {
		const layers = map.getStyle().layers;
		for (var i = 0; i < layers.length; i++) {
			if (layers[i].id.includes('airfinder')) {
				return layers[i].id;
			}
		}
	} catch (e) { }
	return null;
}

function clearLayers(map) {
	let { layers, sources } = map.getStyle();
	layers.forEach((layer) => {
		if (layer.id.includes('-airfinder')) {
			map.removeLayer(layer.id);
		}
	});
	Object.keys(sources).forEach((source) => {
		if (source.includes('-airfinder')) {
			map.removeSource(source);
		}
	});
}

export {
	listToMap,
	getMapStyles,
	getMapInitOptions,
	fitPolygon,
	addPolygonToMap,
	addPointToMap,
	addLayerNameToMap,
	addLayerNameToMapPoint,
	addFillLayerToMap,
	addFillPolygonAndLineToMap,
	pointsInsidePolygon,
	populateFromMap,
	pointsToString,
	checkExistingPointInDraw,
	addPointTodraw,
	drawAreaIndoorPlan,
	drawHistory,
	createMarker,
	removeMarker,
	getFirstLayer,
	clearLayers
}